import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useLogin } from '@/hooks/useLogin';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { ROUTES } from '@/app/router/routes';
import { useOnboardingAnalytics } from './hooks/useOnboardingAnalytics';
import { AuthLayout } from './components/AuthLayout';
import { SignInForm } from './components/SignInForm';
import { ForgotPasswordForm } from './components/ForgotPasswordForm';
import { MobileLoginIntro } from './components/MobileLoginIntro';
import { ProgressHud } from './components/AuthControls';
import styles from './LoginPage.module.css';

/**
 * LoginPage — /login.
 *
 * Mirrors Flutter WebLoginPage (presentation/web/auth/web_login/web_login_page.dart): the
 * onboarding panel and the sign-in form column (WebLoginForms, min height of the screen), the
 * footer, and a progress overlay while signing in. "Forgot Password ?" swaps the form for the
 * forgot password view (state.isWebForgotPassword). On mobile the form column starts with the
 * logo bar, the onboarding texts and image instead.
 *
 * A signed-in user whose email isn't verified goes to /signUp, which shows the verify-email card
 * (Flutter's routing gate shows WebSignUpPage with showVerifyEmailPopup for every route). Not while
 * a login runs: useLogin picks the destination then.
 *
 * Flutter source files:
 *   - presentation/web/auth/web_login/web_login_page.dart
 *   - presentation/web/auth/web_login/web_login_forms.dart
 *   - application/web_auth/web_login/web_login_bloc.dart
 */
export function LoginPage() {
  const location = useLocation();
  const { isAuthorized, user } = useAuthStore();
  const login = useLogin();
  const isMobile = useIsMobile();
  useOnboardingAnalytics();

  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string | null>(null);
  const isForgotPassword = forgotPasswordEmail !== null;

  if (user && !user.emailVerified && !login.isLoading) {
    return <Navigate to={ROUTES.SIGN_UP} replace state={location.state} />;
  }
  // Already signed in — but not mid-login, where useLogin decides the destination
  // (post-login redirect / MFA) once user details load
  if (isAuthorized && !login.isLoading) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return (
    <AuthLayout align="center" formColumnClassName={styles.formColumn}>
      {isMobile && !isForgotPassword && <MobileLoginIntro />}
      {isForgotPassword ? (
        <ForgotPasswordForm
          onBack={() => setForgotPasswordEmail(null)}
          onSubmit={login.handleForgotPassword}
          initialEmail={forgotPasswordEmail}
        />
      ) : (
        <SignInForm login={login} onForgotPassword={setForgotPasswordEmail} />
      )}
      {login.isLoading && <ProgressHud />}
    </AuthLayout>
  );
}
